
import { Task } from '@/types/task';
import { useTaskMutations } from '@/hooks/useTaskMutations';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { exportTasksToCSV, parseCSVTasks } from '@/utils/csvUtils';

export const useTaskActions = (refetchTasks: () => Promise<any>, isAdmin: boolean) => {
  const { toast } = useToast();
  
  const {
    addTaskMutation,
    deleteTaskMutation,
    completeTaskMutation,
    editTaskMutation,
    reactivateTaskMutation,
  } = useTaskMutations();

  const handleAddTask = async (data: any): Promise<boolean> => {
    try {
      await addTaskMutation.mutateAsync(data);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleCompleteTask = (task: Task) => {
    completeTaskMutation.mutate(task);
  };

  const handleDeleteTask = (taskId: string) => {
    if (!isAdmin) {
      toast({
        title: "Permission Denied",
        description: "You do not have permission to delete tasks.",
        variant: "destructive"
      });
      return;
    }
    deleteTaskMutation.mutate(taskId);
  };

  const handleEditTask = (taskId: string, data: any) => {
    if (!isAdmin) {
      toast({
        title: "Permission Denied",
        description: "You do not have permission to edit tasks.",
        variant: "destructive"
      });
      return;
    }
    editTaskMutation.mutate({ taskId, data });
  };

  const handleReactivateTask = (task: Task) => {
    if (!isAdmin) {
      toast({
        title: "Permission Denied",
        description: "You do not have permission to reactivate tasks.",
        variant: "destructive"
      });
      return;
    }
    reactivateTaskMutation.mutate(task);
  };

  const handleExportTasks = (tasks: Task[]) => {
    if (!tasks.length) {
      toast({
        title: "No tasks to export",
        description: "There are no tasks available to export.",
        variant: "destructive"
      });
      return;
    }

    exportTasksToCSV(tasks);
    toast({
      title: "Tasks exported",
      description: "Your tasks have been exported to CSV successfully."
    });
  };

  const handleImportTasks = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const tasks = parseCSVTasks(text);

        for (const task of tasks) {
          await addTaskMutation.mutateAsync(task);
        }

        toast({
          title: "Tasks imported",
          description: `Successfully imported ${tasks.length} tasks.`
        });
      } catch (error) {
        console.error('Error importing tasks:', error);
        toast({
          title: "Import failed",
          description: "There was an error importing the tasks. Please check the file format.",
          variant: "destructive"
        });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleClearDatabase = async () => {
    try {
      const { error } = await supabase.rpc('clear_database');
      if (error) throw error;

      await refetchTasks();
      
      toast({
        title: "Database cleared",
        description: "All tasks and logs have been successfully cleared.",
      });
      
      return true; // Return true to indicate success
    } catch (error) {
      console.error('Error clearing database:', error);
      toast({
        title: "Clear failed",
        description: "There was an error clearing the database. Please try again.",
        variant: "destructive"
      });
      return false;
    }
  };

  return {
    handleAddTask,
    handleCompleteTask,
    handleDeleteTask,
    handleEditTask,
    handleReactivateTask,
    handleExportTasks,
    handleImportTasks,
    handleClearDatabase,
  };
};
